/**
 * Image Picker Component for Puck Editor
 *
 * Allows users to select images from their site assets, stock library, or upload new ones
 */

import React, { useState, useEffect, useRef } from "react";
import { ImageIcon, Upload, X, Check, Loader2, Library } from "lucide-react";
import { FieldLabel } from "@puckeditor/core";
import { runUploads, screenImageFiles } from "../../lib/upload-queue";

interface ImagePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}

/** As the photo list returns it: `url` is resolved server-side, so there is
 *  nothing left here to reconstruct from a key. */
interface Asset {
  id: string;
  url: string;
  filename: string | null;
  altText: string | null;
}

interface StockImage {
  id: string;
  key: string;
  filename: string | null;
  metadata: {
    url?: string;
    deliveryUrl?: string;
    category?: string;
    tags?: string[];
    altText?: string;
  } | null;
  // The list endpoint flattens the join into an array. The old singular
  // `businessType` field was never on the response, which is why the picker
  // could not group by trade.
  businessTypes?: Array<{ id: string; value: string; label: string }>;
}

type TabType = "your-images" | "stock-images";

/** Sentinel for the trade filter's "everything" option. */
const ALL_TRADES = "all";

export const ImagePicker: React.FC<ImagePickerProps> = ({ value = "", onChange, label }) => {
  // Defensive: legacy Puck data may store image fields as objects instead of URL strings.
  // Coerce non-strings to "" so .split / <img src> don't crash the editor.
  const safeValue = typeof value === "string" ? value : "";
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("your-images");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stockImages, setStockImages] = useState<StockImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [tradeFilter, setTradeFilter] = useState<string>(ALL_TRADES);
  // Built from the unfiltered load so the options don't shrink as you filter.
  const [tradeOptions, setTradeOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 400),
      });
    }
  }, [isOpen]);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = (window as any).__SERVICEPRO_CONFIG__;
      if (!config?.businessId || !config?.apiUrl) {
        throw new Error("Site configuration not found");
      }

      const response = await fetch(
        `${config.apiUrl}/api/sites/businesses/${config.businessId}/photos`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch assets");
      }

      const data = await response.json();
      setAssets(data.photos || []);
    } catch (err: any) {
      console.error("Failed to fetch assets:", err);
      setError(err.message || "Failed to load images");
    } finally {
      setLoading(false);
    }
  };

  const fetchStockImages = async (businessTypeId?: string) => {
    setStockLoading(true);
    try {
      const config = (window as any).__SERVICEPRO_CONFIG__;
      if (!config?.apiUrl) {
        throw new Error("API configuration not found");
      }

      const params = new URLSearchParams({ limit: "500" });
      if (businessTypeId && businessTypeId !== ALL_TRADES) {
        params.set("businessTypeId", businessTypeId);
      }

      const response = await fetch(`${config.apiUrl}/api/sites/stock-images?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch stock images");
      }

      const data = await response.json();
      const assets: StockImage[] = data.assets || [];
      setStockImages(assets);

      // Only the unfiltered load can see every trade, so that is the one that
      // defines the options. Filtering must not narrow the menu that produced it.
      if (!businessTypeId || businessTypeId === ALL_TRADES) {
        const byId = new Map<string, string>();
        for (const asset of assets) {
          for (const trade of asset.businessTypes ?? []) {
            byId.set(trade.id, trade.label);
          }
        }
        setTradeOptions(
          Array.from(byId, ([id, label]) => ({ id, label })).sort((a, b) =>
            a.label.localeCompare(b.label),
          ),
        );
      }
    } catch (err: any) {
      console.error("Failed to fetch stock images:", err);
      // Don't show error for stock images - just show empty state
    } finally {
      setStockLoading(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    fetchAssets();
    fetchStockImages();
  };

  /**
   * Uploads every file the picker was handed, a few at a time.
   *
   * Selecting one photo at a time was the only option here, which meant a pro
   * building a gallery reopened this dropdown once per image. The queue reports
   * each file separately, so one bad file no longer sinks the batch.
   */
  const uploadOne = async (file: File): Promise<string> => {
    const config = (window as any).__SERVICEPRO_CONFIG__;
    if (!config?.businessId || !config?.apiUrl) {
      throw new Error("Site configuration not found");
    }
    const auth = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const photosUrl = `${config.apiUrl}/api/sites/businesses/${config.businessId}/photos`;

    // The business photo endpoints, not a generic asset write: they meter the
    // photo limit, verify the bytes reached Cloudflare, dedupe, and queue vision
    // tagging. A photo registered any other way silently skips all four.
    const urlResponse = await fetch(`${photosUrl}/upload-url`, {
      method: "POST",
      headers: auth,
    });
    if (!urlResponse.ok) {
      throw new Error("Failed to get upload URL");
    }

    const { uploadUrl, id: imageId } = await urlResponse.json();

    const formData = new FormData();
    formData.append("file", file);
    const uploadResponse = await fetch(uploadUrl, { method: "POST", body: formData });
    if (!uploadResponse.ok) {
      throw new Error("Failed to upload image");
    }

    const uploadResult = await uploadResponse.json();
    const variants: string[] = uploadResult.result?.variants ?? [];
    // "public" is the variant safe to render without a signed token. No guessed
    // fallback URL: one built from a missing account hash renders a broken image
    // and is stored as though it worked.
    const deliveryUrl = variants.find((v) => v.endsWith("/public")) ?? variants[0];
    if (!deliveryUrl) {
      throw new Error("Upload returned no URL");
    }

    const registerResponse = await fetch(photosUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({
        key: imageId,
        url: deliveryUrl,
        filename: file.name,
        contentType: file.type,
        size: file.size,
      }),
    });
    if (!registerResponse.ok) {
      throw new Error("Uploaded but could not be saved");
    }

    const photo = await registerResponse.json();
    return photo.url as string;
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;

    setUploading(true);
    setError(null);
    setUploadStatus(null);

    const { accepted, rejected } = screenImageFiles(selected);
    let finished = 0;

    try {
      const outcomes = await runUploads(accepted, async (file) => {
        try {
          return await uploadOne(file);
        } finally {
          finished += 1;
          setUploadStatus(`Uploading ${finished} of ${accepted.length}`);
        }
      });

      const uploaded = outcomes.filter((outcome) => outcome.value);
      const failures = outcomes.length - uploaded.length + rejected.length;
      if (failures > 0) {
        setError(
          uploaded.length > 0
            ? `${failures} of ${selected.length} could not be uploaded`
            : "Upload failed",
        );
      }

      if (uploaded.length === 0) return;

      // Refresh so the whole batch is visible, then select the first of them —
      // the picker fills exactly one slot, and the rest are now in the library.
      await fetchAssets();
      const firstUrl = uploaded[0].value!;
      setIsOpen(false);
      setTimeout(() => {
        onChange(firstUrl);
      }, 0);
    } catch (err: any) {
      console.error("Upload failed:", err);
      setError(err.message || "Failed to upload image");
    } finally {
      setUploading(false);
      setUploadStatus(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const getImageUrl = (asset: Asset): string => asset.url;

  const getStockImageUrl = (image: StockImage): string => {
    return image.metadata?.deliveryUrl || image.metadata?.url || image.key;
  };

  const handleSelect = (asset: Asset) => {
    const url = getImageUrl(asset);
    // Close first, then update value to ensure proper state sequencing
    setIsOpen(false);
    // Use setTimeout to ensure the onChange happens after the dropdown close
    // This prevents potential race conditions with React's state batching
    setTimeout(() => {
      onChange(url);
    }, 0);
  };

  const handleStockSelect = (image: StockImage) => {
    const url = getStockImageUrl(image);
    // Close first, then update value to ensure proper state sequencing
    setIsOpen(false);
    // Use setTimeout to ensure the onChange happens after the dropdown close
    setTimeout(() => {
      onChange(url);
    }, 0);
  };

  return (
    <FieldLabel label={label ?? "Image"} el="div">
      <div style={{ position: "relative" }}>
        {/* Current Image Preview / Trigger */}
        <button
          ref={buttonRef}
          type="button"
          onClick={handleOpen}
          style={{
            width: "100%",
            padding: safeValue ? "4px" : "12px",
            fontSize: "14px",
            color: "#6B7280",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "6px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: safeValue ? "flex-start" : "center",
            gap: "8px",
            minHeight: "44px",
          }}
        >
          {safeValue ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
              <img
                crossOrigin="anonymous"
                src={safeValue}
                alt="Selected"
                style={{
                  width: "36px",
                  height: "36px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <span
                style={{
                  flex: 1,
                  textAlign: "left",
                  fontSize: "13px",
                  color: "#374151",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {safeValue.split("/")?.[4] || "Selected image"}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                }}
                style={{
                  padding: "4px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9CA3AF",
                }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <ImageIcon size={18} />
              <span>Select image</span>
            </>
          )}
        </button>

        {/* Dropdown Panel */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              onClick={() => setIsOpen(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
              }}
            />

            {/* Panel */}
            <div
              style={{
                position: "fixed",
                top: `${dropdownPos.top}px`,
                left: `${dropdownPos.left}px`,
                width: `${dropdownPos.width}px`,
                maxWidth: "90vw",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
                zIndex: 1000,
                maxHeight: "400px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header with Tabs and Upload */}
              <div
                style={{
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                {/* Tab Row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px 0 12px",
                  }}
                >
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      type="button"
                      onClick={() => setActiveTab("your-images")}
                      style={{
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: activeTab === "your-images" ? "#374151" : "#9CA3AF",
                        backgroundColor: activeTab === "your-images" ? "#F3F4F6" : "transparent",
                        border: "none",
                        borderRadius: "4px 4px 0 0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <ImageIcon size={14} />
                      Your Images
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab("stock-images")}
                      style={{
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: activeTab === "stock-images" ? "#374151" : "#9CA3AF",
                        backgroundColor: activeTab === "stock-images" ? "#F3F4F6" : "transparent",
                        border: "none",
                        borderRadius: "4px 4px 0 0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Library size={14} />
                      Stock Library
                    </button>
                  </div>
                  {activeTab === "your-images" && (
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        color: uploading ? "#9CA3AF" : "#6366F1",
                        backgroundColor: uploading ? "#F3F4F6" : "#EEF2FF",
                        border: "1px solid",
                        borderColor: uploading ? "#E5E7EB" : "#C7D2FE",
                        borderRadius: "4px",
                        cursor: uploading ? "not-allowed" : "pointer",
                      }}
                    >
                      {uploading ? (
                        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
                      ) : (
                        <Upload size={14} />
                      )}
                      {uploading ? (uploadStatus ?? "Uploading...") : "Upload images"}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleUpload}
                        disabled={uploading}
                        style={{ display: "none" }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    padding: "8px 12px",
                    fontSize: "12px",
                    color: "#DC2626",
                    backgroundColor: "#FEF2F2",
                    borderBottom: "1px solid #FEE2E2",
                  }}
                >
                  {error}
                </div>
              )}

              {/* Image Grid - Your Images Tab */}
              {activeTab === "your-images" && (
                <div
                  style={{
                    padding: "12px",
                    overflowY: "auto",
                    flex: 1,
                  }}
                >
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                        color: "#9CA3AF",
                      }}
                    >
                      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ marginLeft: "8px", fontSize: "13px" }}>Loading images...</span>
                    </div>
                  ) : assets.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "24px",
                        color: "#9CA3AF",
                        fontSize: "13px",
                      }}
                    >
                      No images found. Upload one to get started.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "8px",
                      }}
                    >
                      {assets.map((asset) => {
                        const imageUrl = getImageUrl(asset);
                        const isSelected = value === imageUrl;
                        return (
                          <button
                            key={asset.id}
                            type="button"
                            onClick={() => handleSelect(asset)}
                            style={{
                              position: "relative",
                              aspectRatio: "1",
                              border: "2px solid",
                              borderColor: isSelected ? "#6366F1" : "#E5E7EB",
                              borderRadius: "6px",
                              overflow: "hidden",
                              cursor: "pointer",
                              padding: 0,
                              backgroundColor: "#F9FAFB",
                            }}
                          >
                            <img
                              crossOrigin="anonymous"
                              src={imageUrl}
                              alt={asset.filename || "Asset"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            {isSelected && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "4px",
                                  right: "4px",
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor: "#6366F1",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Check size={12} color="white" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Image Grid - Stock Library Tab */}
              {activeTab === "stock-images" && (
                <div
                  style={{
                    padding: "12px",
                    overflowY: "auto",
                    flex: 1,
                  }}
                >
                  {/* The library spans every trade, so an unfiltered grid shows a
                      plumber photos of lawn care. */}
                  {tradeOptions.length > 0 && (
                    <select
                      value={tradeFilter}
                      onChange={(e) => {
                        setTradeFilter(e.target.value);
                        void fetchStockImages(e.target.value);
                      }}
                      style={{
                        width: "100%",
                        marginBottom: "10px",
                        padding: "6px 8px",
                        fontSize: "13px",
                        border: "1px solid #E5E7EB",
                        borderRadius: "6px",
                        background: "#fff",
                        color: "#374151",
                      }}
                    >
                      <option value={ALL_TRADES}>All trades</option>
                      {tradeOptions.map((trade) => (
                        <option key={trade.id} value={trade.id}>
                          {trade.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {stockLoading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                        color: "#9CA3AF",
                      }}
                    >
                      <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} />
                      <span style={{ marginLeft: "8px", fontSize: "13px" }}>
                        Loading stock images...
                      </span>
                    </div>
                  ) : stockImages.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "24px",
                        color: "#9CA3AF",
                        fontSize: "13px",
                      }}
                    >
                      No stock images available yet.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "8px",
                      }}
                    >
                      {stockImages.map((image) => {
                        const imageUrl = getStockImageUrl(image);
                        const isSelected = value === imageUrl;
                        return (
                          <button
                            key={image.id}
                            type="button"
                            onClick={() => handleStockSelect(image)}
                            style={{
                              position: "relative",
                              aspectRatio: "1",
                              border: "2px solid",
                              borderColor: isSelected ? "#6366F1" : "#E5E7EB",
                              borderRadius: "6px",
                              overflow: "hidden",
                              cursor: "pointer",
                              padding: 0,
                              backgroundColor: "#F9FAFB",
                            }}
                          >
                            <img
                              crossOrigin="anonymous"
                              src={imageUrl}
                              alt={image.metadata?.altText || image.filename || "Stock image"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            {isSelected && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "4px",
                                  right: "4px",
                                  width: "20px",
                                  height: "20px",
                                  borderRadius: "50%",
                                  backgroundColor: "#6366F1",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Check size={12} color="white" />
                              </div>
                            )}
                            {image.metadata?.category && (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "0",
                                  left: "0",
                                  right: "0",
                                  padding: "2px 4px",
                                  backgroundColor: "rgba(0,0,0,0.6)",
                                  fontSize: "9px",
                                  color: "white",
                                  textAlign: "center",
                                  textTransform: "capitalize",
                                }}
                              >
                                {image.metadata.category}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Manual URL Input */}
              <div
                style={{
                  padding: "8px 12px",
                  borderTop: "1px solid #E5E7EB",
                  display: "flex",
                  gap: "8px",
                }}
              >
                <input
                  type="text"
                  placeholder="Or paste image URL..."
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 10px",
                    fontSize: "12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "4px",
                    outline: "none",
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </FieldLabel>
  );
};
